import {GROUND_BACKGROUNDS} from './groundBackgrounds';

/**
 * Тайлы гор — картинка ячейки «гора» под каждую подложку поля: на зимней
 * подложке горы заснеженные, на пустынной — песчаные и так далее.
 *
 * Картинку горы целиком определяет подложка: у всех гор поля она одна. В
 * состоянии партии про тайлы ничего не хранится — ни на сервере, ни в записи
 * матча: подложка там уже есть (gameState.groundBackground), по ней клиент и
 * выбирает тайл. Жетон в hexes при этом остается 'mountain' — на нем держатся
 * правила (проходимость, линия стрельбы, бонусы).
 *
 * Картинки лежат в public/img/hexes/mountains (см. MOUNTAIN_TILE_IMAGES).
 * Сцена грузит ОДНУ картинку — тайл своей подложки (см. GameScene.preload):
 * остальные в партии не нужны.
 */

/**
 * Запасной тайл: им рисуются горы на подложке, которой в перечне ниже нет
 * (новую подложку завели, а свой тайл горы ей не назначили), и гора вне
 * партии — в правилах, обучении и палитре редактора поля
 */
export const FALLBACK_MOUNTAIN_TILE = 'mountain_rock_1';

/**
 * Тайл горы у подложки: идентификатор подложки (см. configs/groundBackgrounds)
 * -> имя тайла. Несколько подложек могут делить один тайл.
 *
 * Добавляя подложку, заведи картинку ее гор (см. MOUNTAIN_TILE_IMAGES) и
 * допиши строку сюда. Без строки горы на этой подложке нарисуются запасным
 * тайлом. Ключи — только существующие подложки: ссылка на снятую с раздачи
 * (закомментированную в GROUND_BACKGROUNDS) превращается в ключ undefined
 */
const MOUNTAIN_TILE_BY_GROUND_BACKGROUND = Object.freeze({
  [GROUND_BACKGROUNDS.MAGICAL_FOREST_2]: 'mountain_magical',
  [GROUND_BACKGROUNDS.SALT_1]: 'mountain_salt',
  [GROUND_BACKGROUNDS.GREEN_1]: 'mountain_green_1',
  [GROUND_BACKGROUNDS.GREEN_2]: 'mountain_green_2',
  [GROUND_BACKGROUNDS.GREEN_3]: 'mountain_green_1',
  [GROUND_BACKGROUNDS.GREEN_4]: 'mountain_green_1',
  [GROUND_BACKGROUNDS.DESERT_1]: 'mountain_dune',
  [GROUND_BACKGROUNDS.DESERT_2]: 'mountain_desert',
  [GROUND_BACKGROUNDS.WINTER_1]: 'mountain_winter',
  [GROUND_BACKGROUNDS.WINTER_2]: 'mountain_winter',
  [GROUND_BACKGROUNDS.WINTER_3]: 'mountain_winter',
  [GROUND_BACKGROUNDS.WINTER_4]: 'mountain_winter',
  [GROUND_BACKGROUNDS.WINTER_5]: 'mountain_ice',
  [GROUND_BACKGROUNDS.DEMON_1]: 'mountain_demon',
  [GROUND_BACKGROUNDS.ASH_1]: 'mountain_ash',
  [GROUND_BACKGROUNDS.GEYSER_1]: 'mountain_geyser',
  [GROUND_BACKGROUNDS.BONE_1]: 'mountain_bone',
  [GROUND_BACKGROUNDS.ISLAND_1]: 'mountain_island',
  [GROUND_BACKGROUNDS.RED_1]: 'mountain_desert',
  [GROUND_BACKGROUNDS.VOLCANO_1]: 'mountain_black',
  [GROUND_BACKGROUNDS.BOG_1]: 'mountain_bog',
  [GROUND_BACKGROUNDS.MUSHROOM_1]: 'mountain_mushroom',
  [GROUND_BACKGROUNDS.PLATO_1]: 'mountain_plato',
  [GROUND_BACKGROUNDS.RICE_1]: 'mountain_rice',
  [GROUND_BACKGROUNDS.SPIRAL_1]: 'mountain_spiral',
});

/**
 * Тайл горы для подложки поля. Имя тайла — оно же ключ текстуры в сцене
 * @param {string|null} [groundBackgroundId] - идентификатор подложки
 * @returns {string} имя тайла; для неизвестной подложки — запасной
 */
export const getMountainTile = (groundBackgroundId) => (
  // Только свои ключи: подложка приходит и из файла состояния, а имя вроде
  // «constructor» нашлось бы в прототипе объекта и подменило бы тайл
  Object.prototype.hasOwnProperty.call(MOUNTAIN_TILE_BY_GROUND_BACKGROUND, groundBackgroundId)
    ? MOUNTAIN_TILE_BY_GROUND_BACKGROUND[groundBackgroundId]
    : FALLBACK_MOUNTAIN_TILE
);

/**
 * Картинки тайлов: имя тайла -> файл в public/img/hexes/mountains.
 *
 * Пути записаны литералами, и в имени файла стоит отпечаток содержимого: так
 * у обновленной картинки меняется адрес, и браузер игрока не достает из кеша
 * старую. Отпечаток ставит и правит здесь скрипт (npm run version:token-art,
 * см. scripts/versionTokenArt.js), руками его не пишут.
 *
 * Новый тайл: положи картинку в папку под обычным именем (mountain_x.png),
 * допиши сюда строку с этим именем и запусти скрипт
 */
const MOUNTAIN_TILE_IMAGES = Object.freeze({
  mountain_rock_1: '/img/hexes/mountains/mountain_rock_1.862dc5c3.png',
  mountain_ash: '/img/hexes/mountains/mountain_ash.8456c93a.png',
  mountain_black: '/img/hexes/mountains/mountain_black.1375eb4c.png',
  mountain_bog: '/img/hexes/mountains/mountain_bog.67df6bd3.png',
  mountain_bone: '/img/hexes/mountains/mountain_bone.7c122600.png',
  mountain_demon: '/img/hexes/mountains/mountain_demon.5d131db5.png',
  mountain_desert: '/img/hexes/mountains/mountain_desert.fe644652.png',
  mountain_dune: '/img/hexes/mountains/mountain_dune.4c2c0d0b.png',
  mountain_geyser: '/img/hexes/mountains/mountain_geyser.691a746e.png',
  mountain_green_1: '/img/hexes/mountains/mountain_green_1.0ccbe622.png',
  mountain_green_2: '/img/hexes/mountains/mountain_green_2.e27a3e4f.png',
  mountain_ice: '/img/hexes/mountains/mountain_ice.2937b47e.png',
  mountain_island: '/img/hexes/mountains/mountain_island.400eb43e.png',
  mountain_magical: '/img/hexes/mountains/mountain_magical.103fa311.png',
  mountain_mushroom: '/img/hexes/mountains/mountain_mushroom.39eebc1a.png',
  mountain_plato: '/img/hexes/mountains/mountain_plato.493aa145.png',
  mountain_rice: '/img/hexes/mountains/mountain_rice.bc37b60b.png',
  mountain_salt: '/img/hexes/mountains/mountain_salt.ad36cf35.png',
  mountain_spiral: '/img/hexes/mountains/mountain_spiral.ef793b88.png',
  mountain_winter: '/img/hexes/mountains/mountain_winter.de606f88.png',
});

/**
 * Файл картинки тайла
 * @param {string} tileId - имя тайла
 * @returns {string} путь от корня public; для неизвестного тайла — картинка
 *   запасного
 */
export const getMountainTileImage = (tileId) => (
  Object.prototype.hasOwnProperty.call(MOUNTAIN_TILE_IMAGES, tileId)
    ? MOUNTAIN_TILE_IMAGES[tileId]
    : MOUNTAIN_TILE_IMAGES[FALLBACK_MOUNTAIN_TILE]
);

/**
 * Картинка горы для подложки поля — то, что игрок видит на поле этой партии
 * @param {string|null} [groundBackgroundId] - идентификатор подложки
 * @returns {string} путь от корня public
 */
export const getMountainImage = (groundBackgroundId) => getMountainTileImage(getMountainTile(groundBackgroundId));

/** Картинка горы вне партии: правила, обучение, палитра редактора поля */
export const DEFAULT_MOUNTAIN_IMAGE = getMountainTileImage(FALLBACK_MOUNTAIN_TILE);

/** Все тайлы гор: назначенные подложкам и запасной — каждый по одному разу */
export const MOUNTAIN_TILES = Object.freeze(
  [...new Set([...Object.values(MOUNTAIN_TILE_BY_GROUND_BACKGROUND), FALLBACK_MOUNTAIN_TILE])]
);

/** Подложки, которым назначен свой тайл горы (для проверок перечня) */
export const GROUND_BACKGROUNDS_WITH_MOUNTAIN_TILE = Object.freeze(Object.keys(MOUNTAIN_TILE_BY_GROUND_BACKGROUND));

/** Тайлы, у которых есть своя картинка (для проверок перечня) */
export const MOUNTAIN_TILES_WITH_IMAGE = Object.freeze(Object.keys(MOUNTAIN_TILE_IMAGES));
